import { useContext, useEffect, useState } from "react"

import { Button, Card, Col, Flex, Row, Select, Skeleton } from "antd"
import dayjs, { Dayjs } from "dayjs"

import variables from "@/_styles/variables.module.scss"
import { CustomRangePicker } from "@/components/_Shared/CustomRangePicker/CustomRangePicker"
import { CustomerContext } from "@/context/CustomerContext"

import classes from "./FinanceReportTable.module.scss"

import { useCustomerQuery } from "@/hook/Dictionaries/Customers/useCustomerQuery"
import { useFinanceReportQuery } from "@/hook/useFinanceQuery"

import { filterSelectOption } from "@/utils/filterSelectOption"
import { getMoney } from "@/utils/formatNumber"


export const FinanceReportTable = () => {
    const userContext = useContext(CustomerContext)
    const isCustomer = userContext?.isCustomer()

    const { data: customers } = useCustomerQuery()
    const [customerId, setCustomerId] = useState<number | null>(null)

    const [isPeriodActive, setIsPeriodActive] = useState<boolean>(false)
    const [activeStartDate, setActiveStartDate] = useState<Dayjs | null>(null)
    const [activeEndDate, setActiveEndDate] = useState<Dayjs | null>(null)

    const { data: financeReport, isLoading: financeIsLoading } = useFinanceReportQuery(
        {
            customerId: customerId,
            startDate: activeStartDate,
            endDate: activeEndDate
        }
    )

    useEffect(() => {
        const customerId = userContext?.customer?.id
        if (isCustomer && customerId) {
            setCustomerId(customerId)
        }
    }, [userContext])

    const onCustomerChange = (customerId: number) => {
        setCustomerId(customerId)
    }

    const onRangeChange = (dates: any) => {
        if (!dates) {
            setActiveStartDate(null)
            setActiveEndDate(null)

            return
        }

        setActiveStartDate(dates[0])
        setActiveEndDate(dates[1])
    }

    return (
        <Skeleton loading={financeIsLoading}>
            <Flex>
                {!isCustomer && 
                    <Select
                        className={classes.axisSelect}
                        size="large"
                        placeholder="Выбор контрагента"
                        onChange={onCustomerChange}
                        value={customerId}
                        options={customers?.response.data.map((customer) => ({
                            value: customer.id,
                            label: customer.name
                        }))}
                        filterOption={(inputValue, option) =>
                            filterSelectOption(inputValue, option?.label || "")
                        }
                        showSearch
                        allowClear
                    />
                }

                <Button
                    className={classes.axisSelect}
                    type={isPeriodActive ? "primary" : "default"}
                    style={{ 
                        borderColor: isPeriodActive ? variables.secondBasecolor : variables.basecolorlight,
                        color: isPeriodActive ? variables.baseBgColorContent : variables.basecolorlight
                    }}
                    shape="round"
                    onClick={() => setIsPeriodActive(!isPeriodActive)}
                >
                    Выбрать период
                </Button>
                {isPeriodActive &&
                    <CustomRangePicker
                        className={classes.axisSelect}
                        value={[activeStartDate ? dayjs(activeStartDate) : undefined, activeEndDate ? dayjs(activeEndDate) : undefined]}
                        onChange={onRangeChange}
                    />
                }
            </Flex>

            <Card>
                <Flex justify="space-around" align="center" gap="large">
                    <Flex className={classes.table} vertical gap="middle">
                        <Card className={classes.card}>
                            <Flex vertical>
                                <div className={classes.gray}>
                                    Баланс на начало периода
                                </div>
                                <div className={classes.price}>
                                    {getMoney(financeReport?.response?.startBalance ?? 0)}
                                </div>
                            </Flex>
                        </Card>
                        <Card className={classes.card}>
                            <Flex vertical>
                                <div className={classes.gray}>
                                    Баланс на конец периода
                                </div>
                                <div className={classes.price}>
                                    {getMoney(financeReport?.response?.endBalance ?? 0)}
                                </div>
                            </Flex>
                        </Card>
                    </Flex>

                    <Flex className={classes.table} vertical justify="space-between" gap="small">
                        <Row className={classes.rowBordered}>
                            <Col className={classes.gray}>
                                Заказы
                            </Col>
                            <Col className={classes.price}>
                                {getMoney(financeReport?.response?.totalCompletedOrders ?? 0)}
                            </Col>
                        </Row>
                        <Row className={classes.rowBordered}>
                            <Col className={classes.gray}>
                                Отмены
                            </Col>
                            <Col className={classes.price}>
                                {getMoney(financeReport?.response?.totalCanceledOrders ?? 0)}
                            </Col>
                        </Row>
                        <Row className={classes.row}>
                            <Col className={classes.gray}>
                                Услуги
                            </Col>
                            <Col className={classes.price}>
                                {getMoney(financeReport?.response?.totalDeliveries ?? 0)}
                            </Col>
                        </Row>
                    </Flex>
                </Flex>
            </Card>
        </Skeleton>
    )
}